import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import { 
  FileText,
  Calendar,
  Clock,
  MapPin,
  User,
  DollarSign,
  CheckCircle2,
  Briefcase,
  Users,
  Plane,
  Hotel,
  Car,
  Mail,
  Phone
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

const PALETTE = TEMPLATE_PALETTES.executive_brief;

const ExecutiveBriefTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    // Parse date in local timezone to avoid UTC offset issues
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const date = new Date(`2000-01-01T${timeString}`);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return timeString;
    }
  };

  // Get client name
  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.lastName}, ${quote.client.firstName}`;
    }
    return 'Client';
  };

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Header - Minimal, Document Style */}
      <div className="">
        <div className="max-w-6xl mx-auto px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-1">Travel Brief</div>
              <h1 className="text-2xl font-bold text-[var(--foreground)]">
                {quote.quoteTitle || 'Executive Travel Itinerary'}
              </h1>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-[var(--muted-foreground)] mb-1">Reference</div>
              <div className="font-mono text-sm font-bold text-[var(--foreground)]">{quote.quoteNumber}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Executive Summary Table */}
      <div className="max-w-6xl mx-auto px-8 py-8">
        <div className="border border-[var(--border)] overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-[var(--border)]">
                <td className="px-4 py-3 bg-[var(--muted)] font-semibold text-[var(--muted-foreground)] w-48">Traveler</td>
                <td className="px-4 py-3 font-mono">{getClientName()}</td>
              </tr>
              {quote.travelDates?.start && (
                <tr className="border-b border-[var(--border)]">
                  <td className="px-4 py-3 bg-[var(--muted)] font-semibold text-[var(--muted-foreground)]">Travel Dates</td>
                  <td className="px-4 py-3 font-mono">
                    {formatDate(quote.travelDates.start)} - {formatDate(quote.travelDates.end)}
                  </td>
                </tr>
              )}
              {(quote.travelers?.adults || quote.travelers?.children?.length) && (
                <tr className="border-b border-[var(--border)]">
                  <td className="px-4 py-3 bg-[var(--muted)] font-semibold text-[var(--muted-foreground)]">Party Size</td>
                  <td className="px-4 py-3 font-mono">
                    {quote.travelers.adults || 0} Adult(s)
                    {quote.travelers.children?.length > 0 && `, ${quote.travelers.children.length} Child(ren)`}
                  </td>
                </tr>
              )}
              <tr>
                <td className="px-4 py-3 bg-[var(--muted)] font-semibold text-[var(--muted-foreground)]">Total Investment</td>
                <td className="px-4 py-3 font-mono font-bold text-[var(--foreground)]">{formatCurrency(quote.totalPrice)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Itinerary Components - Spreadsheet Style */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-[var(--foreground)] mb-4 uppercase tracking-wide">Detailed Itinerary</h2>
          
          {quote.components && quote.components.length > 0 ? (
            <div className="border border-[var(--border)] overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-[var(--primary)] text-[var(--background)]">
                    <th className="px-4 py-3 text-left font-semibold">#</th>
                    <th className="px-4 py-3 text-left font-semibold">Type</th>
                    <th className="px-4 py-3 text-left font-semibold">Description</th>
                    <th className="px-4 py-3 text-left font-semibold">Details</th>
                    <th className="px-4 py-3 text-right font-semibold">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.components.map((component, index) => (
                    <React.Fragment key={component.id || index}>
                      <tr className="border-b border-[var(--border)] hover:bg-[var(--muted)]">
                        <td className="px-4 py-4 font-mono text-[var(--muted-foreground)]">{index + 1}</td>
                        <td className="px-4 py-4">
                          <span className="inline-block px-2 py-1 bg-[var(--muted)] text-xs font-semibold uppercase tracking-wide">
                            {component.type || 'Item'}
                          </span>
                        </td>
                        <td className="px-4 py-4 font-semibold">{component.title || `Component ${index + 1}`}</td>
                        <td className="px-4 py-4">
                          {component.data && Object.keys(component.data).length > 0 && (
                            <div className="space-y-1 text-xs">
                              {Object.entries(component.data).map(([key, value]) => {
                                if (!value || key === 'isOption' || key === 'optionDescription') return null;
                                
                                // Check if this is a price field
                                const isPriceField = key.toLowerCase().includes('price') || 
                                                     key.toLowerCase().includes('cost') || 
                                                     key.toLowerCase().includes('fee') ||
                                                     key.toLowerCase().includes('total') ||
                                                     key.toLowerCase().includes('deposit');
                                const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                                const isTime = key.toLowerCase().includes('time');
                                const isConfirmation = key.toLowerCase().includes('confirmation') || 
                                                       key.toLowerCase().includes('reference') ||
                                                       key.toLowerCase().includes('booking');
                                
                                let displayValue = String(value);
                                if (isPriceField && isNumeric) {
                                  displayValue = formatCurrency(parseFloat(value));
                                }
                                
                                return (
                                  <div key={key} className="flex gap-2">
                                    <span className="text-[var(--muted-foreground)] uppercase tracking-wide min-w-32">
                                      {humanizeKey(key)}:
                                    </span>
                                    <span className={`${isTime || isConfirmation ? 'font-mono font-semibold' : ''} text-[var(--foreground)]`}>
                                      {displayValue}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right font-mono font-bold">{formatCurrency(component.price)}</td>
                      </tr>
                      <tr className="border-b border-[var(--border)]">
                        <td colSpan="5" className="px-4 py-4">
                          {/* Component Photos */}
                          {component.photos && component.photos.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-[var(--border)]">
                              <h4 className="text-xs font-bold text-[var(--foreground)] mb-3 uppercase tracking-wider flex items-center gap-2">
                                <span>📸</span> Attachments
                              </h4>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {component.photos.map((photo, photoIndex) => (
                                  <div 
                                    key={photoIndex} 
                                    className="relative group cursor-pointer rounded overflow-hidden shadow hover:shadow-md transition-shadow border border-[var(--border)] bg-white"
                                    onClick={() => window.open(photo.url, '_blank')}
                                  >
                                    <img 
                                      src={photo.url} 
                                      alt={photo.name || 'Component photo'} 
                                      className="w-full h-auto object-contain"
                                      style={{ maxHeight: '300px' }}
                                    />
                                    <div className="absolute inset-0 bg-gray-900/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                      <span className="text-[var(--background)] text-xs font-semibold">{photo.name || 'View'}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Component Documents */}
                          {component.documents && component.documents.length > 0 && (
                            <div className="mt-4">
                              <h4 className="text-xs font-bold text-[var(--foreground)] mb-2 uppercase tracking-wider flex items-center gap-2">
                                <span>📋</span> Documents
                              </h4>
                              <div className="space-y-2">
                                {component.documents.map((doc, docIndex) => (
                                  <a 
                                    key={docIndex}
                                    href={doc.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 bg-[var(--muted)] hover:bg-[var(--muted)] px-3 py-2 rounded border border-[var(--border)] text-sm font-mono"
                                  >
                                    <span>📄</span>
                                    <span className="text-[var(--foreground)]">{doc.name}</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {hasMultipleOptions(component) && (
                            <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                              <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {getComponentOptions(component).map((option, optIndex) => (
                                  <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                                    {option.photoUrl && (
                                      <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                                    )}
                                    <div className="p-4">
                                      <div className="flex items-center justify-between gap-2 mb-1">
                                        <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                        {option.badge && (
                                          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                        )}
                                      </div>
                                      {option.description && (
                                        <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                                      )}
                                      <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                  
                  {/* Total Row */}
                  <tr className="bg-[var(--muted)] font-bold">
                    <td colSpan="4" className="px-4 py-4 text-right uppercase tracking-wide">Total</td>
                    <td className="px-4 py-4 text-right font-mono text-lg">{formatCurrency(quote.totalPrice)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border border-[var(--border)] p-8 text-center text-[var(--muted-foreground)]">
              No itinerary items
            </div>
          )}
        </div>

        {/* Payment Summary */}
        {quote.depositAmount > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-bold text-[var(--foreground)] mb-4 uppercase tracking-wide">Payment Schedule</h2>
            <div className="border border-[var(--border)] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[var(--muted)]">
                    <th className="px-4 py-3 text-left font-semibold">Payment Type</th>
                    <th className="px-4 py-3 text-left font-semibold">Due Date</th>
                    <th className="px-4 py-3 text-right font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-[var(--border)]">
                    <td className="px-4 py-3">Initial Deposit</td>
                    <td className="px-4 py-3 font-mono">{quote.depositDueDate ? formatDate(quote.depositDueDate) : 'Upon Booking'}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">{formatCurrency(quote.depositAmount)}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Final Payment</td>
                    <td className="px-4 py-3 font-mono">TBD</td>
                    <td className="px-4 py-3 text-right font-mono font-bold">
                      {formatCurrency(quote.totalPrice - quote.depositAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notes Section */}
        {quote.notes && (
          <div className="mt-8">
            <h2 className="text-lg font-bold text-[var(--foreground)] mb-4 uppercase tracking-wide">Additional Information</h2>
            <div className="border border-[var(--border)] p-6 bg-[var(--muted)]">
              <p className="text-sm text-[var(--foreground)] whitespace-pre-wrap leading-relaxed">
                {quote.notes}
              </p>
            </div>
          </div>
        )}

        {/* Action Section */}
        {onPayment && (
          <div className="mt-8 border-t-2 primary-border-[var(--border)] pt-8">
            <button
              onClick={onPayment}
              disabled={isAgentPreview}
              className={`w-full bg-[var(--primary)] text-[var(--background)] font-semibold py-4 px-6 uppercase tracking-wider text-sm transition-all ${
                isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110'
              }`}
            >
              {isAgentPreview ? 'Preview Mode - Payment Disabled' : 'Proceed to Payment'}
            </button>
            {!isAgentPreview && (
              <p className="text-center text-xs text-[var(--muted-foreground)] mt-4 uppercase tracking-wide">
                Secure Payment Processing
              </p>
            )}
          </div>
        )}

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 border-t border-[var(--border)] pt-8">
            <div className="bg-[var(--muted)] border border-[var(--border)] rounded p-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Briefcase className="w-5 h-5 text-[var(--muted-foreground)]" />
                <h3 className="text-sm font-bold text-[var(--foreground)] uppercase tracking-wider">Executive Travel Coordinator</h3>
              </div>
              
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-lg font-bold text-[var(--foreground)]">{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="text-[var(--muted-foreground)] text-sm font-semibold">{quote.agent_business_name}</p>
                  )}
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
                {quote.agent_email && (
                  <a 
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] font-mono"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{quote.agent_email}</span>
                  </a>
                )}
                
                {quote.agent_email && quote.agent_phone && (
                  <span className="hidden sm:block border-text-[var(--foreground)]">|</span>
                )}
                
                {quote.agent_phone && (
                  <a 
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] font-mono"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-[var(--border)] text-center">
          <p className="text-xs text-[var(--muted-foreground)] uppercase tracking-wider">
            Executive Travel Services • Confidential Document
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveBriefTemplate;
